/**
 * Native shell detection + integration (Phase 13 / M11).
 *
 * The same React bundle ships to three runtimes:
 *
 *   1. Plain browser (no native shell)
 *   2. Tauri 2.x desktop shell (apps/native/desktop)
 *   3. Capacitor 7.x mobile shell (apps/native/mobile)
 *
 * Both native shells inject a `window.__chaos` object alongside their own
 * platform-specific globals before the React app boots. This module is the
 * single, type-safe place that decides which surface to talk to.
 *
 * Why a single module?
 *   - Push registration differs per shell: Web Push (browser),
 *     `window.__chaos.push.register()` (Capacitor), N/A (Tauri — desktops
 *     surface notifications through the existing native plugin, not via push
 *     servers).
 *   - Notification rendering differs: web Notification API vs.
 *     `dispatchEvent('chaos:notify')` events bridged to the OS.
 *   - The rest of the app should NOT care which shell it is in — it just
 *     calls `detectShell()`, `showNotification(...)`, or `registerPush(...)`
 *     and gets the right behaviour for free.
 *
 * Design notes:
 *   - No imports from `@tauri-apps/*` or `@capacitor/*`. The production web
 *     bundle has neither installed (they live only in the native shell
 *     workspaces). We probe via `window.__TAURI_INTERNALS__` /
 *     `window.Capacitor` and use the `window.__chaos` event surface that
 *     each shell injects.
 *   - All native interactions go through `window.dispatchEvent(CustomEvent)`
 *     or `window.__chaos.*` so this module stays bundler-agnostic.
 *   - Detection is synchronous + cached for the lifetime of the page.
 */

export type ShellKind = 'web' | 'tauri' | 'capacitor';

export type PushProvider = 'web' | 'apns' | 'fcm';

export interface NativePushRegistration {
  /** Opaque token. For Web Push this is the JSON endpoint+keys envelope as
   *  a string; for FCM/APNs this is the device token returned by the OS. */
  token: string;
  /** Source of the token. */
  provider: PushProvider;
}

export interface NativeNotificationPayload {
  title: string;
  body: string;
  /** Optional deep-link path inside the web app, e.g. `/cards/abc`. */
  url?: string;
  /** Optional grouping tag — replaces an earlier banner with the same id. */
  tag?: string;
  /** Optional category (passed through to Tauri). */
  category?: 'approval' | 'blocker' | 'build_failure' | 'agent_completed' | 'info';
}

interface TauriGlobals {
  __TAURI_INTERNALS__?: unknown;
  __TAURI__?: unknown;
}

interface CapacitorGlobalShape {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

interface CapacitorGlobals {
  Capacitor?: CapacitorGlobalShape;
}

interface ChaosShellApi {
  platform?: string;
  push?: {
    register?: () => Promise<string | null>;
  };
}

interface ChaosGlobals {
  __chaos?: ChaosShellApi;
}

type GlobalShell = TauriGlobals & CapacitorGlobals & ChaosGlobals;

function readWindow(): GlobalShell | null {
  if (typeof window === 'undefined') return null;
  return window as unknown as GlobalShell;
}

let cachedShell: ShellKind | null = null;

/**
 * Detect which shell hosts the chaos-ui bundle.
 *
 * Returns:
 *   - `'tauri'` — running inside the Tauri 2.x desktop shell. Detected via
 *     the `__TAURI_INTERNALS__` global injected by the runtime in Tauri 2,
 *     with a fallback to the legacy `__TAURI__` global.
 *   - `'capacitor'` — running inside Capacitor 7.x. Detected via the
 *     `Capacitor` global; we trust `isNativePlatform()` when it returns
 *     true. (`Capacitor` exists in dev mode in a browser too but reports
 *     `isNativePlatform() === false`, in which case we fall back to web.)
 *   - `'web'` — anything else (browser, headless, vitest with jsdom).
 *
 * The result is cached after the first call. Call `resetShellDetection()`
 * in tests to clear the cache.
 */
export function detectShell(): ShellKind {
  if (cachedShell !== null) return cachedShell;

  const w = readWindow();
  if (!w) {
    cachedShell = 'web';
    return cachedShell;
  }

  if (typeof w.__TAURI_INTERNALS__ !== 'undefined' || typeof w.__TAURI__ !== 'undefined') {
    cachedShell = 'tauri';
    return cachedShell;
  }

  const capacitor = w.Capacitor;
  if (capacitor && typeof capacitor.isNativePlatform === 'function') {
    try {
      if (capacitor.isNativePlatform()) {
        cachedShell = 'capacitor';
        return cachedShell;
      }
    } catch {
      /* fall through */
    }
  }

  cachedShell = 'web';
  return cachedShell;
}

/** Test-only escape hatch — clears the cached detection result. */
export function resetShellDetection(): void {
  cachedShell = null;
}

/**
 * Force the detected shell for tests / dev tooling. Production callers
 * should rely on auto-detection.
 */
export function setShellForTesting(value: ShellKind | null): void {
  cachedShell = value;
}

/**
 * Surface a notification through whichever channel best fits the current
 * shell. The web fallback uses the standard `Notification` constructor;
 * the native shells take the payload via `CustomEvent('chaos:notify')`
 * which their bridge converts into an OS-level toast.
 *
 * The function is best-effort: it never throws, it never blocks. If the
 * shell can't show a banner (permission denied, missing API), it returns
 * `false`.
 */
export async function showNotification(payload: NativeNotificationPayload): Promise<boolean> {
  const shell = detectShell();

  if (shell === 'tauri' || shell === 'capacitor') {
    try {
      const event = new CustomEvent('chaos:notify', {
        detail: {
          title: payload.title,
          body: payload.body,
          ...(payload.url ? { action_url: payload.url } : {}),
          ...(payload.tag ? { tag: payload.tag } : {}),
          category: payload.category ?? 'info',
        },
      });
      window.dispatchEvent(event);
      return true;
    } catch {
      return false;
    }
  }

  if (typeof window === 'undefined' || typeof window.Notification !== 'function') {
    return false;
  }
  if (Notification.permission !== 'granted') {
    if (Notification.permission === 'denied') return false;
    try {
      const result = await Notification.requestPermission();
      if (result !== 'granted') return false;
    } catch {
      return false;
    }
  }
  try {
    const options: NotificationOptions = {
      body: payload.body,
    };
    if (payload.tag) options.tag = payload.tag;
    if (payload.url) options.data = { url: payload.url };
    new Notification(payload.title, options);
    return true;
  } catch {
    return false;
  }
}

/**
 * Register the current shell with whatever push provider it has access to.
 *
 *   - **Web:** returns the Web Push subscription as a JSON-stringified DTO
 *     containing the endpoint + p256dh + auth keys. The caller (push.ts)
 *     is responsible for parsing it back and forwarding to the server.
 *   - **Capacitor:** awaits the FCM (Android) or APNS (iOS) token via the
 *     bridge surface and returns it verbatim. The platform field tells
 *     the backend which provider to dispatch through.
 *   - **Tauri:** desktop shells do not register for remote push — they
 *     receive notifications through the existing Tauri notification
 *     plugin, driven by the same backend events but rendered locally.
 *     We return `null` so the caller can skip server registration.
 *
 * Errors are converted to `null` to keep the call site simple; the caller
 * surfaces a user-visible error via the higher-level `push.ts` wrapper.
 */
export async function registerPush(): Promise<NativePushRegistration | null> {
  const shell = detectShell();

  if (shell === 'tauri') {
    return null;
  }

  if (shell === 'capacitor') {
    const w = readWindow();
    const register = w?.__chaos?.push?.register;
    if (typeof register !== 'function') return null;
    try {
      const token = await register();
      if (!token) return null;
      const platform = w?.Capacitor?.getPlatform?.() ?? 'unknown';
      const provider: PushProvider = platform === 'ios' ? 'apns' : 'fcm';
      return { token, provider };
    } catch {
      return null;
    }
  }

  // Web — push.ts owns the full Web Push flow. We don't duplicate it here;
  // callers that opt into the native-bridge surface for web get a
  // sentinel `null` and fall back to the existing browser path.
  return null;
}

/**
 * Convenience helper for runtime branches that want to know whether the
 * shell can deliver out-of-tab notifications without any extra user
 * interaction. True for Tauri (always granted at install), true for
 * Capacitor once `registerPush` has succeeded, true for browsers with
 * granted permission.
 */
export function hasBackgroundNotificationChannel(): boolean {
  const shell = detectShell();
  if (shell === 'tauri') return true;
  if (shell === 'capacitor') return true;
  if (typeof window === 'undefined' || typeof window.Notification !== 'function') {
    return false;
  }
  return Notification.permission === 'granted';
}
